/**
 * URL safety helpers for the JSON template renderer.
 *
 * The renderer interpolates user-controlled invitation data (including the
 * free-form `customData` payload, which the backend only validates as an
 * opaque object) into `href` and `src` attributes. Without scheme filtering,
 * a creator could store `javascript:…` or `data:text/html,…` in a field that
 * a template binds to a link/image, producing stored XSS on the public,
 * guest-facing invitation page.
 *
 * These helpers allow only schemes that are safe to navigate to or load, and
 * fall back to `null` (render nothing) for everything else.
 */

// Schemes permitted for navigable links (`<a href>` / button targets).
const SAFE_LINK_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:']);

// Schemes permitted for media (`<img src>`). `data:` images are allowed
// because templates legitimately inline small images; `javascript:` is not.
const SAFE_IMAGE_SCHEMES = new Set(['http:', 'https:', 'data:']);

function scheme(raw: string): string | null {
  // Strip ASCII control chars and spaces before reading the scheme — browsers
  // ignore them in URLs, so `java\tscript:` must be treated as `javascript:`.
  // eslint-disable-next-line no-control-regex
  const cleaned = raw.replace(/[\u0000-\u0020\u007f]/g, '');
  const match = cleaned.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):/);
  return match ? `${match[1].toLowerCase()}:` : null;
}

/**
 * Returns the URL if it is safe to use as a link target, otherwise `null`.
 * Relative URLs and same-page anchors (`#…`, `/…`, `foo`) are allowed —
 * they cannot carry a dangerous scheme.
 */
export function safeLinkUrl(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const value = raw.trim();
  if (!value) return null;
  const s = scheme(value);
  if (s === null) return value; // schemeless → relative/anchor, safe
  return SAFE_LINK_SCHEMES.has(s) ? value : null;
}

/**
 * Returns the URL if it is safe to use as an image source, otherwise `null`.
 */
export function safeImageUrl(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const value = raw.trim();
  if (!value) return null;
  const s = scheme(value);
  if (s === null) return value; // schemeless → relative path, safe
  return SAFE_IMAGE_SCHEMES.has(s) ? value : null;
}
